/*
 * aizome-mega-menu.js
 * Mounts the custom nav (3 tabs + block-driven dropdowns) into the native
 * Horizon header's left pill and drives open/close with hover-intent:
 * the menu stays open while moving toward / over the panel and only closes
 * after a short delay. Desktop only; mobile keeps the native menu/drawer.
 */
const AMM_CLOSE_DELAY = 220; // ms grace period before a panel closes

class AizomeNav extends HTMLElement {
  connectedCallback() {
    const nav = this.querySelector('.amm-nav');
    if (!nav) return;
    this.navHTML = nav.outerHTML;
    this.closeTimer = null;

    this.mount();
    this.syncStripColor();
    // Re-mount if Horizon re-renders the header (sticky clone / section API).
    this.observer = new MutationObserver(() => this.mount());
    const header = document.querySelector('#header-component');
    if (header) this.observer.observe(header, { childList: true, subtree: true });

    this._onKey = (e) => { if (e.key === 'Escape') this.closeAll(); };
    document.addEventListener('keydown', this._onKey);
    this._onResize = () => this.syncStripColor();
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('keydown', this._onKey);
    window.removeEventListener('resize', this._onResize);
  }

  /* Colour the PAGE WRAPPER (behind everything) to match the first content
     section, so the transparent header area inherits that colour without the
     header itself having a solid background (which would cover content on
     scroll). */
  syncStripColor() {
    const sections = Array.from(document.querySelectorAll('.shopify-section')).filter(
      (s) =>
        !s.className.includes('header-group') &&
        !s.className.includes('footer-group') &&
        s.getBoundingClientRect().height > 30
    );
    const first = sections[0];
    if (!first) return;

    let color = null;
    const bgEl = first.querySelector('.section-background');
    if (bgEl) color = this.opaqueBg(bgEl);
    if (!color) {
      const all = [first, ...first.querySelectorAll('*')];
      for (const el of all) {
        color = this.opaqueBg(el);
        if (color) break;
      }
    }

    if (color) {
      const wrap = document.querySelector('.page-width-narrow') || document.body;
      wrap.style.setProperty('background-color', color);
    }
  }

  opaqueBg(el) {
    const c = getComputedStyle(el).backgroundColor;
    if (c && c !== 'rgba(0, 0, 0, 0)' && c !== 'transparent') return c;
    return null;
  }

  mount() {
    const left = document.querySelector('.header__column--left');
    if (!left) return;
    let nav = left.querySelector('.amm-nav');
    if (!nav) {
      left.insertAdjacentHTML('beforeend', this.navHTML);
      nav = left.querySelector('.amm-nav');
    }
    if (nav && !nav.dataset.ammWired) {
      nav.dataset.ammWired = '1';
      this.wire(nav);
    }
  }

  wire(nav) {
    this.wraps = Array.from(nav.querySelectorAll('.amm-tab-wrap'));
    this.wraps.forEach((wrap) => {
      if (!wrap.querySelector('.amm-panel')) return; // plain link tab, no dropdown
      wrap.addEventListener('mouseenter', () => {
        clearTimeout(this.closeTimer);
        this.open(wrap);
      });
      wrap.addEventListener('mouseleave', () => {
        clearTimeout(this.closeTimer);
        this.closeTimer = setTimeout(() => this.closeAll(), AMM_CLOSE_DELAY);
      });
    });
  }

  open(wrap) {
    (this.wraps || []).forEach((w) => {
      if (w !== wrap) this.setOpen(w, false);
    });
    this.setOpen(wrap, true);
  }

  closeAll() {
    (this.wraps || []).forEach((w) => this.setOpen(w, false));
  }

  setOpen(wrap, isOpen) {
    wrap.classList.toggle('is-open', isOpen);
    const tab = wrap.querySelector('.amm-tab');
    if (tab) tab.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }
}

if (!customElements.get('aizome-nav')) {
  customElements.define('aizome-nav', AizomeNav);
}

/* Mounts the same block-based menu into Horizon's native mobile drawer as
   tap-to-expand accordion tabs. Reuses the native hamburger, slide-in,
   backdrop, scroll-lock and close button. */
class AizomeMobileMenu extends HTMLElement {
  connectedCallback() {
    const menu = this.querySelector('.amm-m');
    if (!menu) return;
    this.menuHTML = menu.outerHTML;

    this.mount();
    this.observer = new MutationObserver(() => this.mount());
    const drawer = document.querySelector('header-drawer');
    if (drawer) this.observer.observe(drawer, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  mount() {
    const nav = document.querySelector('.menu-drawer__navigation');
    if (!nav || nav.querySelector('.amm-m')) return;
    nav.insertAdjacentHTML('afterbegin', this.menuHTML);
    this.wire(nav.querySelector('.amm-m'));
  }

  wire(root) {
    const items = Array.from(root.querySelectorAll('.amm-m-item'));
    items.forEach((item) => {
      const tab = item.querySelector('.amm-m-tab');
      if (!tab || tab.tagName !== 'BUTTON') return; // plain link tab
      tab.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-open');
        items.forEach((other) => {
          other.classList.remove('is-open');
          const t = other.querySelector('.amm-m-tab');
          if (t && t.tagName === 'BUTTON') t.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          tab.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }
}

if (!customElements.get('aizome-mobile-menu')) {
  customElements.define('aizome-mobile-menu', AizomeMobileMenu);
}
